import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    Sparkles,
    Coins,
    ShoppingCart,
    Send,
    Loader2,
    CheckCircle2,
    ArrowUpRight,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import AIPanel from "@/components/AIPanel";
import CommercialOfferBanner from "@/components/CommercialOfferBanner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Dashboard() {
    const { user, refresh } = useAuth();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [aiPanelOpen, setAiPanelOpen] = useState(false);

    const [prompt, setPrompt] = useState("Give me 3 B2B lead-gen tips.");
    const [asking, setAsking] = useState(false);
    const [response, setResponse] = useState(null);

    const [buying, setBuying] = useState(false);

    if (!user) return null;

    const ask = async () => {
        if (!prompt.trim()) return;
        setAsking(true);
        setResponse(null);
        try {
            const res = await api.askAi(prompt.trim());
            setResponse(res.response);
            await refresh();
            toast.success(`1 credit consumed · ${res.credits_remaining} left`);
        } catch (e) {
            if (e.status === 402) {
                toast.error("Insufficient credits — buy some below.");
            } else {
                toast.error(e.message || "AI request failed");
            }
        } finally {
            setAsking(false);
        }
    };

    const buy = async () => {
        setBuying(true);
        try {
            const { url } = await api.createCheckout("starter_1000");
            window.location.href = url;
        } catch (e) {
            toast.error(e.message || "Checkout failed");
            setBuying(false);
        }
    };

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar
                collapsed={sidebarCollapsed}
                onToggle={() => setSidebarCollapsed((c) => !c)}
            />

            <div className="flex-1 min-w-0 flex flex-col">
                <Header />

                <main
                    data-testid="dashboard-main"
                    className="flex-1 min-w-0 px-6 md:px-8 py-8 max-w-6xl w-full"
                >
                    {/* Welcome */}
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mb-8"
                    >
                        <p className="text-[11px] uppercase tracking-widest text-text-secondary">
                            Dashboard
                        </p>
                        <h1 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight">
                            Welcome back,{" "}
                            <span
                                data-testid="welcome-username"
                                className="gradient-text"
                            >
                                {user.name}
                            </span>
                            .
                        </h1>
                        <p className="mt-1 text-sm text-text-secondary">
                            Phase 1 online: prova il Lead Engine, testa l'AI,
                            top-up crediti.
                        </p>
                    </motion.div>

                    {/* Phase 7B — dynamic offer banner (only renders when eligible) */}
                    <CommercialOfferBanner />

                    {/* Bento grid */}
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                        {/* Credits Balance */}
                        <motion.section
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.35, delay: 0.05 }}
                            data-testid="credits-card"
                            className="md:col-span-2 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5"
                        >
                            <div className="flex items-center justify-between">
                                <span className="h-9 w-9 rounded-lg bg-[var(--color-panel-ai)] grid place-items-center border border-[var(--color-border)]">
                                    <Coins className="h-4 w-4 text-[var(--color-secondary)]" />
                                </span>
                                <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                                    Balance
                                </span>
                            </div>
                            <div className="mt-5">
                                <p
                                    data-testid="credits-balance-value"
                                    className="text-4xl font-semibold tabular-nums tracking-tight"
                                >
                                    {user.credits ?? 0}
                                </p>
                                <p className="mt-1 text-sm text-text-secondary">
                                    Credits available
                                </p>
                            </div>
                            <div className="mt-6 h-1 rounded-full bg-[var(--color-panel-ai)] overflow-hidden">
                                <div
                                    className="h-full gradient-bg-cta"
                                    style={{
                                        width: `${Math.min(
                                            100,
                                            ((user.credits ?? 0) / 1000) * 100,
                                        )}%`,
                                    }}
                                />
                            </div>
                            <p className="mt-3 text-xs text-text-secondary">
                                1 credit ≈ 1 AI call
                            </p>
                        </motion.section>

                        {/* Test OpenAI */}
                        <motion.section
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.35, delay: 0.1 }}
                            data-testid="ai-test-card"
                            className="md:col-span-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="h-9 w-9 rounded-lg bg-[var(--color-panel-ai)] grid place-items-center border border-[var(--color-border)]">
                                        <Sparkles className="h-4 w-4 text-[var(--color-secondary)]" />
                                    </span>
                                    <div>
                                        <p className="text-sm font-medium">
                                            Test OpenAI
                                        </p>
                                        <p className="text-xs text-text-secondary">
                                            gpt-4o-mini · 1 credit / request
                                        </p>
                                    </div>
                                </div>
                                <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                                    POC
                                </span>
                            </div>

                            <textarea
                                data-testid="ai-prompt-input"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                rows={3}
                                placeholder="Ask something…"
                                className="mt-4 w-full resize-none rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-3.5 py-3 text-sm text-text-main placeholder:text-text-secondary/70 focus:outline-none focus:border-[var(--color-primary)]/60 transition-colors"
                            />

                            <div className="mt-3 flex items-center justify-between">
                                <p className="text-xs text-text-secondary">
                                    {prompt.length}/4000
                                </p>
                                <button
                                    data-testid="ai-ask-btn"
                                    onClick={ask}
                                    disabled={asking || !prompt.trim()}
                                    className="inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2.5 text-sm font-medium text-white transition-transform duration-150 hover:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {asking ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Thinking…
                                        </>
                                    ) : (
                                        <>
                                            <Send className="h-4 w-4" />
                                            Ask AI
                                        </>
                                    )}
                                </button>
                            </div>

                            {response && (
                                <motion.div
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-4"
                                    data-testid="ai-response"
                                >
                                    <div className="flex items-center gap-1.5 text-xs text-[var(--color-success)]">
                                        <CheckCircle2 className="h-3.5 w-3.5" />
                                        Response
                                    </div>
                                    <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap">
                                        {response}
                                    </p>
                                </motion.div>
                            )}
                        </motion.section>

                        {/* Buy Credits */}
                        <motion.section
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.35, delay: 0.15 }}
                            data-testid="buy-credits-card"
                            className="md:col-span-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 relative overflow-hidden"
                        >
                            <div
                                aria-hidden
                                className="absolute -right-16 -top-16 h-52 w-52 rounded-full blur-3xl opacity-40"
                                style={{
                                    background:
                                        "radial-gradient(closest-side, rgba(255, 255, 255,0.4), transparent)",
                                }}
                            />
                            <div className="relative">
                                <div className="flex items-center gap-2">
                                    <span className="h-9 w-9 rounded-lg bg-[var(--color-panel-ai)] grid place-items-center border border-[var(--color-border)]">
                                        <ShoppingCart className="h-4 w-4 text-[var(--color-secondary)]" />
                                    </span>
                                    <div>
                                        <p className="text-sm font-medium">
                                            Buy Credits
                                        </p>
                                        <p className="text-xs text-text-secondary">
                                            Stripe · Test mode
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <p className="text-3xl font-semibold tracking-tight">
                                        1,000 credits
                                    </p>
                                    <p className="mt-1 text-sm text-text-secondary">
                                        One-time top up
                                    </p>
                                </div>

                                <div className="mt-6 flex items-center justify-between">
                                    <p className="text-2xl font-semibold tabular-nums">
                                        $19
                                    </p>
                                    <button
                                        data-testid="buy-credits-btn"
                                        onClick={buy}
                                        disabled={buying}
                                        className="inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2.5 text-sm font-medium text-white transition-transform duration-150 hover:scale-[0.98] disabled:opacity-60"
                                    >
                                        {buying ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Redirecting…
                                            </>
                                        ) : (
                                            <>
                                                Checkout
                                                <ArrowUpRight className="h-4 w-4" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </motion.section>

                        {/* Small tip card */}
                        <motion.section
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.35, delay: 0.2 }}
                            className="md:col-span-3 rounded-2xl border border-dashed border-[var(--color-border)] bg-transparent p-5"
                        >
                            <p className="text-[11px] uppercase tracking-widest text-text-secondary">
                                What's next
                            </p>
                            <p className="mt-3 text-sm leading-relaxed">
                                Il{" "}
                                <span className="text-[var(--color-secondary)]">
                                    Lead Engine
                                </span>{" "}
                                è live: discovery autonoma, enrichment e AI
                                scoring 0–100. Fase 2 aggiungerà Outreach
                                automatico sulle liste che stai già salvando.
                            </p>
                            <p className="mt-3 text-xs text-text-secondary">
                                Le altre feature sono in roadmap — vedi le voci
                                bloccate in sidebar.
                            </p>
                        </motion.section>
                    </div>
                </main>
            </div>

            <AIPanel
                open={aiPanelOpen}
                onToggle={() => setAiPanelOpen((v) => !v)}
            />
        </div>
    );
}
